import React, { useState } from "react";
import { LogIn, LogOut, Maximize2, ImageOff, ExternalLink, MapPin, Pencil, Trash2 } from "lucide-react";
import { matchPercent, mapsUrl } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AuthImage } from "@/components/AuthImage";
import { EvidenceManager } from "@/components/EvidenceManager";
import { PhotoLightbox } from "@/components/PhotoLightbox";
import { PunchEditDialog } from "@/components/PunchEditDialog";
import { StatusBadge } from "@/components/StatusBadge";
import { ReasonDialog } from "@/components/ReasonDialog";
import { fmtKLDateTime } from "@/lib/api";
import { useLang, reasonLabel } from "@/lib/i18n";

/* Shared evidence card used by the supervisor queue, the admin punch table and
   the Reports drill-down. onDelete is supplied for reviewers (admins AND
   supervisors since Batch 39 — the backend scopes supervisors to their
   locations). Deleted punches are archived server-side, never destroyed. */
export const PunchCard = ({ punch, onReview, busy, selectable, selected, onSelectChange, onDelete }) => {
  const { t } = useLang();
  const [enlarged, setEnlarged] = useState(false);
  const [dialogMode, setDialogMode] = useState(null); // 'flag' | 'override'
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const p = punch;
  const eligible = p.reviewStatus === "pending" && p.matchResult === "pass" && p.locationStatus === "ok";

  return (
    <Card data-testid="punch-card" className="shadow-sm">
      <CardContent className="flex flex-col gap-3 pt-4 sm:flex-row">
        {selectable && (
          <div className="flex items-start pt-1">
            <Checkbox
              data-testid="punch-select-checkbox"
              checked={selected}
              disabled={!eligible}
              onCheckedChange={(v) => onSelectChange(Boolean(v))}
            />
          </div>
        )}
        <button
          type="button"
          data-testid="punch-photo-thumbnail"
          onClick={() => p.photoId && setEnlarged(true)}
          disabled={!p.photoId}
          title={!p.photoId ? t("photoRemoved") : undefined}
          className="group relative h-24 w-24 shrink-0 overflow-hidden rounded-lg border border-border"
        >
          {p.photoId ? (
            <>
              <AuthImage photoId={p.photoId} thumb className="h-full w-full" />
              <span className="absolute bottom-1 right-1 rounded bg-black/50 p-0.5 opacity-0 transition-opacity duration-150 group-hover:opacity-100">
                <Maximize2 className="h-3 w-3 text-white" />
              </span>
            </>
          ) : (
            <span
              data-testid="punch-photo-removed"
              className="flex h-full w-full flex-col items-center justify-center gap-1 bg-secondary p-1 text-center text-[10px] leading-tight text-muted-foreground"
            >
              <ImageOff className="h-4 w-4" />
              {t("photoRemoved")}
            </span>
          )}
        </button>

        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold">{p.userName}</span>
            <span className="text-xs text-muted-foreground">{p.employeeId}</span>
            <span className="inline-flex items-center gap-1 text-xs font-medium">
              {p.punchType === "clock_in"
                ? <><LogIn className="h-3.5 w-3.5 text-emerald-700" /> {t("clockInLabel")}</>
                : <><LogOut className="h-3.5 w-3.5 text-sky-700" /> {t("clockOutLabel")}</>}
            </span>
          </div>
          <p className="font-mono text-sm tabular-nums" data-testid="punch-server-time">
            {fmtKLDateTime(p.serverTimestamp)}
            <Button
              size="icon" variant="ghost"
              data-testid="punch-edit-button"
              title={t("editPunch")}
              onClick={() => setEditOpen(true)}
              className="ml-1 h-6 w-6 align-middle text-muted-foreground hover:text-foreground"
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            {onDelete && (
              <Button
                size="icon" variant="ghost"
                data-testid="punch-delete-button"
                title={t("deletePunch")}
                onClick={() => setDeleteOpen(true)}
                className="ml-0.5 h-6 w-6 align-middle text-muted-foreground hover:text-destructive"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            )}
          </p>
          <div className="flex flex-wrap items-center gap-1.5">
            <StatusBadge kind="review" value={p.reviewStatus} />
            <StatusBadge kind="match" value={p.matchResult} />
            {p.matchScore != null && (
              <span
                title={`Euclidean distance: ${p.matchScore.toFixed(3)}`}
                className="rounded-full border border-border bg-secondary px-2 py-0.5 font-mono text-xs tabular-nums"
                data-testid="punch-match-score"
              >
                {t("matchResult")}: {matchPercent(p.matchScore)}%
              </span>
            )}
            <StatusBadge kind="location" value={p.locationStatus} />
            {p.siteName && (
              <span className="inline-flex items-center gap-1 rounded-full border border-border bg-secondary px-2 py-0.5 text-xs font-medium" data-testid="punch-site-badge">
                <MapPin className="h-3 w-3 text-primary" /> {p.siteName}
              </span>
            )}
            {p.manual && (
              <span
                data-testid="punch-manual-badge"
                className="rounded-full bg-sky-50 px-2 py-0.5 text-xs font-semibold text-sky-900"
              >
                {t("manualBadge")}{p.addedByName ? ` · ${p.addedByName}` : ""}
              </span>
            )}
            {p.editedAt && (
              <span
                data-testid="punch-edited-badge"
                title={p.originalTimestamp ? `${t("originalTime")}: ${fmtKLDateTime(p.originalTimestamp)}` : undefined}
                className="rounded-full bg-violet-50 px-2 py-0.5 text-xs font-semibold text-violet-900"
              >
                {t("editedBadge")}{p.editedByName ? ` · ${p.editedByName}` : ""}
              </span>
            )}
            {p.shiftName && (
              <span className="rounded-full border border-border bg-secondary px-2 py-0.5 text-xs" data-testid="punch-shift-badge">
                {t("shiftLabel")}: {p.shiftName}
              </span>
            )}
            {p.minutesLate != null && (
              <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-900" data-testid="punch-late-badge">
                +{p.minutesLate} {t("minLate")}
              </span>
            )}
            {p.minutesEarly != null && (
              <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-900" data-testid="punch-early-badge">
                −{p.minutesEarly} {t("minEarly")}
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            {p.latitude != null ? (
              <>
                {p.placeName && (
                  <span data-testid="punch-place-name">
                    ≈{p.placeDistanceKm != null ? `${p.placeDistanceKm} km ` : ""}{t("nearLabel")} <span className="font-medium text-foreground/80">{p.placeName}</span> ·{" "}
                  </span>
                )}
                <span className="font-mono tabular-nums">{p.latitude.toFixed(5)}, {p.longitude.toFixed(5)}</span>
                {p.accuracyM != null && <> · {t("accuracy")}: {Math.round(p.accuracyM)} m</>}
                {" · "}
                <a
                  href={mapsUrl(p.latitude, p.longitude)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="punch-maps-link"
                  className="inline-flex items-center gap-0.5 text-primary underline-offset-2 hover:underline"
                >
                  {t("openMap")} <ExternalLink className="h-3 w-3" />
                </a>
              </>
            ) : (
              <>{t("coordinates")}: —</>
            )}
          </p>
          {p.summary && (
            <p className="rounded-lg bg-secondary p-2 text-xs" data-testid="punch-summary">
              <span className="font-semibold text-muted-foreground">{t("summary")}: </span>
              {p.summary}
            </p>
          )}
          {p.manual && (
            <div className="rounded-lg bg-secondary/60 p-2">
              <p className="mb-1.5 text-xs font-semibold text-muted-foreground">{t("evidence")}</p>
              <EvidenceManager punch={p} />
            </div>
          )}
          {p.reviewStatus !== "pending" && p.reviewedByName && (
            <p className="text-xs text-muted-foreground">
              {t("reviewedBy")}: {p.reviewedByName}
              {p.overrideReasonCode && <> · {reasonLabel(t, p.overrideReasonCode)}</>}
              {(p.overrideNote || p.flagNote) && <> — “{p.overrideNote || p.flagNote}”</>}
            </p>
          )}
        </div>

        {onReview && p.reviewStatus === "pending" && (
          <div className="flex shrink-0 flex-row gap-2 sm:flex-col">
            <Button size="sm" data-testid="review-approve-button" disabled={busy}
              onClick={() => onReview(p.id, { action: "approve" })}
              className="bg-emerald-700 hover:bg-emerald-700/90">
              {t("approve")}
            </Button>
            <Button size="sm" variant="secondary" data-testid="review-flag-button" disabled={busy}
              onClick={() => setDialogMode("flag")}>
              {t("flag")}
            </Button>
            <Button size="sm" variant="outline" data-testid="review-override-button" disabled={busy}
              onClick={() => setDialogMode("override")}>
              {t("override")}
            </Button>
          </div>
        )}
      </CardContent>

      <PhotoLightbox
        open={enlarged}
        onOpenChange={setEnlarged}
        photoId={p.photoId}
        comparePunchId={p.id}
        title={`${p.userName} — ${p.punchType === "clock_in" ? t("clockInLabel") : t("clockOutLabel")}`}
        subtitle={`${fmtKLDateTime(p.serverTimestamp)}${p.matchScore != null ? ` · ${t("matchResult")}: ${matchPercent(p.matchScore)}%` : ""}`}
      />

      {onReview && (
        <ReasonDialog
          open={dialogMode !== null}
          onOpenChange={(v) => !v && setDialogMode(null)}
          mode={dialogMode}
          busy={busy}
          onSubmit={({ note, reasonCode }) => onReview(p.id, { action: dialogMode, note, reasonCode })}
        />
      )}

      <PunchEditDialog open={editOpen} onOpenChange={setEditOpen} punch={p} />

      {onDelete && (
        <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
          <AlertDialogContent data-testid="punch-delete-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>{t("deletePunchTitle")}</AlertDialogTitle>
              <AlertDialogDescription>
                <span className="font-medium text-foreground">{p.userName}</span>
                {" · "}{fmtKLDateTime(p.serverTimestamp)}
                <br />{t("deletePunchBody")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="punch-delete-cancel">{t("cancel")}</AlertDialogCancel>
              <AlertDialogAction
                data-testid="punch-delete-confirm"
                disabled={busy}
                onClick={() => onDelete(p.id)}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {t("delete")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </Card>
  );
};
